import { observable, action, runInAction, computed } from "mobx";
import { Subscription } from "rxjs";

import { MainOfferStore, BettingTypeSelectorsStore } from "@gp/offer";
import { ISubscriptionRequest } from "@gp/hub";
import { EventType, LiveStatus } from "@gp/models";
import { ConsoleLogger, LogLevel } from "@gp/utility";

import { getCurrentCulture } from "@utils";
import bettingRules from "@betting-rules";
import { LiveOfferMenuStore } from "@state/stores/offer/live/LiveOfferMenuStore";
import RootStore from "@state/stores/RootStore";
import { history, redirect, getCurrentLiveSection } from "@state";
import { analyticsTracker } from "@utils/analytics";
import { LiveEventTypeTrackingEnum } from "@data-types/analytics-tracker/AnalyticsTrackerEnum";

class BaseLiveEventMenuViewStore extends MainOfferStore {
	@observable public expandedElementId: string | null;
	@observable public isLoading = true;

	SELECTED_EVENTS_LIMIT = 5;

	public rootStore: RootStore;
	firstLoad: boolean = true;
	protected liveOfferMenuStore: LiveOfferMenuStore;
	@observable private rawSelectedEventIds = "";

	private subscription: Subscription | null;

	@computed public get expandedElement() {
		if (this.expandedElementId) {
			return (
				this.eventsInSports.sports.find(
					(s) => s.id === this.expandedElementId
				) || null
			);
		}

		return null;
	}

	@computed public get elementSubMenu() {
		return this.expandedElement?.events || [];
	}

	@computed public get isMenuExpanded() {
		return this.rootStore.isSideMenuExpanded;
	}

	@computed public get eventIds() {
		// slice ensures to remove first empty element because of leading '/'
		return this.rawSelectedEventIds.split("/").slice(1);
	}

	@computed public get cleanedEventIds() {
		return this.eventIds.map((eventId) => eventId.split("-")[0]);
	}

	@computed public get orderEvents() {
		return this.eventsInSports.sports.reduce((acc: string[], sport) => {
			acc.push(...(sport?.events?.map((e) => e.id) || []));
			return acc;
		}, []);
	}

	constructor(rootStore: RootStore, liveOfferMenuStore: LiveOfferMenuStore) {
		super({
			logger: new ConsoleLogger(false, LogLevel.ERROR),
			removeDelay: 10,
			throttle: 4,
			enableThrottling: true,
			customConfiguration: bettingRules,
			bettingTypeSelectorsStore: new BettingTypeSelectorsStore(),
		});

		this.rootStore = rootStore;
		this.liveOfferMenuStore = liveOfferMenuStore;
	}

	@action.bound
	public onInitialize() {
		this.onLoad();
	}

	@action
	protected async onLoad(subsReq: ISubscriptionRequest | null = null) {
		const subscriptionRequest: ISubscriptionRequest = subsReq || {
			subscriptionId: "live-event-sidemenu-offer",
			compress: WEBPACK_OFFER_COMPRESS,
			channels: [
				{
					name: "event",
					filter: {
						eventType: EventType.NORMAL,
						liveStatus: LiveStatus.LIVE,
					},
				},
			],
		};

		this.subscription = this.rootStore.hub
			.getOfferSubscription(subscriptionRequest)
			.subscribe({
				next: (response) => {
					runInAction(() => {
						this.assignOfferData(response);

						// Used to set initial open sport in the menu
						if (
							this.eventsInSports.sports.length > 0 &&
							response.startingVersion === 0
						) {
							this.initialSportExpandOnResponse();

							if (
								this.eventsInSports.sports.length > 1 &&
								(history.location.state
									?.eventIdsForEventSwitcher?.length || 0) ==
									0
							) {
								history.replace({
									state: {
										...history.location?.state,
										eventIdsForEventSwitcher:
											this.eventsInSports.sports.reduce(
												(acc, sport) => {
													sport.events?.forEach(
														(event) => {
															acc.push(event.id);
														}
													);

													return acc;
												},
												[] as string[]
											),
									},
								});
								this.rootStore.eventSwitcherViewStore.onInitialize();
							}
						}
						this.isLoading = false;
					});
				},
				error: (err) => {
					console.error(err);
					runInAction(() => {
						this.isLoading = false;
					});
				},
			});
	}

	@action.bound
	private initialSportExpandOnResponse() {
		const availableEvents = this.cleanedEventIds.filter((id) =>
			this.eventsMap.has(id)
		);

		const lastEventId =
			availableEvents.length > 0
				? availableEvents[availableEvents.length - 1]
				: "";

		const lastEvent = this.eventsMap.get(lastEventId);

		if (lastEvent) {
			this.expandElement(lastEvent.sportId);
		} else {
			// this is failsafe
			if (this.firstLoad && this.liveOfferMenuStore.selectedSport) {
				this.expandElement(
					this.liveOfferMenuStore.selectedSport.node.id
				);
				this.firstLoad = false;
				return;
			}
			this.expandElement(this.eventsInSports.sports[0].id);
		}
	}

	//#region actions

	@action.bound
	public expandCollapseElement(elId: string) {
		if (this.expandedElementId !== elId || !this.isMenuExpanded) {
			this.expandElement(elId);
		} else {
			this.expandedElementId = null;
			this.rootStore.closeSideMenu();
		}
	}

	@action.bound
	public expandElement(elId: string) {
		this.expandedElementId = elId;
		this.rootStore.openSideMenu();
	}

	@action.bound
	public expandCollapseMenu() {
		this.rootStore.toggleSideMenu();
	}

	@action.bound
	public setSelectedEventIds(rawEventIds: string) {
		const formattedRawIds = "/" + rawEventIds;

		if (this.rawSelectedEventIds === formattedRawIds) {
			return;
		}

		this.rawSelectedEventIds = formattedRawIds;
	}

	@action.bound
	public addEvent(eventId: string): void {
		if (this.rawSelectedEventIds.indexOf(eventId) === -1) {
			analyticsTracker.trackLiveEventInteraction(
				LiveEventTypeTrackingEnum.LiveMultiEventAdded
			);
		}
		if (this.cleanedEventIds.length + 1 > this.SELECTED_EVENTS_LIMIT) {
			const url = this.rawSelectedEventIds.split("/").slice(2).join("/");
			this.rawSelectedEventIds = `/${url}`;
		}
		if (this.rawSelectedEventIds.indexOf(eventId) === -1) {
			this.rawSelectedEventIds += `/${eventId}`;
			this._updateUrl();
		}
	}

	@action.bound
	public removeEvent(eventId: string): void {
		const eventUrlInfo = this.eventIds.find((entry) =>
			entry.includes(eventId)
		);

		if (eventUrlInfo) {
			this.rawSelectedEventIds = this.rawSelectedEventIds.replace(
				`/${eventUrlInfo}`,
				""
			);

			const prev = window.history.state;
			history.replace({
				...history.location,
				state: {
					...prev,
					eventIdsForEventSwitcher: this.orderEvents,
				},
			});
			this._updateUrl();
		}
	}

	//#endregion actions

	//#region disposers

	@action.bound
	public onDispose() {
		this.disposeSubscription();
		this.firstLoad = true;
		this.rawSelectedEventIds = "";
		this.reset();
		this.expandedElementId = null;
		this.rootStore.closeSideMenu();
	}

	@action.bound
	private disposeSubscription() {
		if (this.subscription) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}
	}

	//#endregion disposers

	//#region helpers

	@action.bound
	private _updateUrl(): void {
		const culture = getCurrentCulture();
		redirect(
			`/${culture}/live/${getCurrentLiveSection()}${
				this.eventIds.length > 0 ? "/" : ""
			}${this.eventIds.join("/")}`,
			true
		);
	}

	//#endregion helpers
}

export default BaseLiveEventMenuViewStore;
